import { ChangeEvent, FC, KeyboardEvent, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

interface MeetChatComposerProps {
  draft: string;
  isSendingImage?: boolean;
  disabled?: boolean;
  onDraftChange: (value: string) => void;
  onSend: () => void | Promise<boolean>;
  onImageSelect: (file: File) => void | Promise<boolean>;
}

export const MeetChatComposer: FC<MeetChatComposerProps> = ({
  draft,
  isSendingImage = false,
  disabled = false,
  onDraftChange,
  onSend,
  onImageSelect,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const maxTextareaHeightPx = 120;

  const syncTextareaHeight = () => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, maxTextareaHeightPx);
    textarea.style.height = `${nextHeight}px`;
  };

  useLayoutEffect(() => {
    syncTextareaHeight();
  }, [draft]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void onSend();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      void onImageSelect(selectedFile);
    }

    event.target.value = '';
  };

  const controlHeightClass = 'h-11 box-border';

  return (
    <div className="shrink-0 border-t border-[#E5E5E5] bg-white p-3 pb-[max(12px,env(safe-area-inset-bottom,0px))] sm:p-4">
      <div className="flex items-end gap-2 sm:gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          type="button"
          disabled={disabled || isSendingImage}
          onClick={() => fileInputRef.current?.click()}
          className={classNames(
            'inline-flex w-10 shrink-0 cursor-pointer items-center justify-center rounded-2xl border border-[#D9D9D9] text-[#2C3038] transition-colors hover:border-[#DEC05E] hover:bg-[#FAF7EA] disabled:cursor-not-allowed disabled:opacity-50 sm:w-11',
            controlHeightClass,
          )}
          aria-label="Выбрать изображение"
          title="Выбрать изображение"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M12 16V8M8 12H16M5 19H19C20.1 19 21 18.1 21 17V7C21 5.9 20.1 5 19 5H5C3.9 5 3 5.9 3 7V17C3 18.1 3.9 19 5 19Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <textarea
          ref={textareaRef}
          value={draft}
          disabled={disabled}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Введите сообщение..."
          rows={1}
          className={classNames(
            'no-scrollbar min-h-11 max-h-[120px] min-w-0 flex-1 resize-none overflow-y-auto rounded-2xl border border-[#D9D9D9] px-3 py-2 text-sm leading-5 text-[#111026] outline-none focus:border-[#DEC05E] disabled:cursor-not-allowed sm:px-4',
            controlHeightClass,
          )}
        />
        <button
          type="button"
          disabled={disabled || draft.trim().length === 0}
          onClick={() => void onSend()}
          className={classNames(
            'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-2xl bg-[#2C3038] text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:bg-[#B0B0B0]',
            'w-11 px-0 sm:w-auto sm:px-4',
            controlHeightClass,
          )}
          aria-label="Отправить"
        >
          <svg className="sm:hidden" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M5 12H19M19 12L13 6M19 12L13 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="hidden sm:inline">Отправить</span>
        </button>
      </div>
      {isSendingImage && <p className="mt-2 text-xs text-[#777]">Отправляем изображение...</p>}
    </div>
  );
};
